import { deriveAppUrl } from '@/lib/appUrl';
import { getActiveUid, isImpersonating } from '@/lib/getActiveUid';
import { isPasswordlessAccount } from '@/lib/providers';
import { isValid } from '@/lib/redirect';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from './auth';
import { useUserStore } from './user';

export interface SidebarOption {
  goto?: string;
  icon: string;
  textKey: string;
  selected?: boolean;
  requiresUser: boolean;
  isSwitchAccount?: boolean;
  preAction?: () => Promise<void>;
}

export const useSidebarStore = defineStore('sidebar', () => {
  const route = useRoute();
  const router = useRouter();
  const selectedOption = ref(-1);
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const currentPath = computed(() => route.path);

  const isBilling = computed(() => currentPath.value.includes('/billing'));
  const isProfile = computed(() => currentPath.value.includes('/account/profile'));
  const isPassword = computed(() => currentPath.value.includes('/password'));
  const isShare = computed(() => currentPath.value.includes('/account/share'));

  const options = computed<SidebarOption[]>(() => [
    {
      goto: getAppUrl(),
      icon: 'arrow-left',
      textKey: 'sidebar_start_writing',
      selected: false,
      requiresUser: true,
    },
    {
      goto: '/account/profile',
      icon: 'profile',
      textKey: 'sidebar_user_profile',
      selected: isProfile.value,
      requiresUser: true,
    },
    // SSO-only accounts have no password to manage — a Password page would
    // only confuse. It stays reachable by URL for the emailed set-a-password
    // support flow.
    ...(isPasswordlessAccount(userStore.user)
      ? []
      : [
          {
            goto: '/account/password',
            icon: 'rename',
            textKey: 'sidebar_password',
            selected: isPassword.value,
            requiresUser: true,
          },
        ]),
    {
      goto: '/billing/portal',
      icon: 'billing',
      textKey: 'sidebar_billing_nav',
      selected: isBilling.value,
      requiresUser: true,
    },
    {
      goto: '/account/share',
      icon: 'gift',
      textKey: 'sidebar_share_dabble',
      selected: isShare.value,
      requiresUser: true,
    },
    {
      isSwitchAccount: true,
      icon: 'user-switch',
      textKey: 'sidebar_switch_account',
      requiresUser: true,
    },
    {
      goto: '/',
      icon: 'sign-out',
      textKey: 'sidebar_sign_out',
      requiresUser: true,
      preAction: async () => {
        await authStore.logout();
      },
    },
  ]);

  function getAppUrl() {
    return withActiveAccount(resolveAppTarget());
  }

  function resolveAppTarget() {
    const stored = sessionStorage.getItem('redirect');
    if (stored && isValid(stored) && isCrossOrigin(stored)) return stored;

    const referrer = document.referrer;
    if (referrer) {
      try {
        const ref = new URL(referrer);
        if (ref.origin !== location.origin && isValid(referrer)) return referrer;
      } catch {
        // Unparseable referrer — fall through to the same host-derived default
        // as the no-referrer case below, so local/test don't get sent to prod.
        return deriveAppUrl();
      }
    }

    return deriveAppUrl();
  }

  /**
   * A stored redirect that points back at accounts is never "the app". The
   * `/billing/*` auth screens pin `${origin}/billing/portal` on route entry, and
   * an already-signed-in visitor never consumes it — reading it here would aim
   * "Start Writing" at the billing portal the user is already looking at. Same
   * rule the referrer branch above applies.
   */
  function isCrossOrigin(target: string) {
    try {
      return new URL(target).origin !== location.origin;
    } catch {
      return false;
    }
  }

  // Forward the account you're viewing here so the writer lands on the same one,
  // instead of falling back to its own default. Mirrors dabble-writer-3.0's
  // account links (?uid=/?impersonate=).
  function withActiveAccount(target: string) {
    const uid = getActiveUid();
    if (!uid) return target;
    try {
      const url = new URL(target);
      url.searchParams.set(isImpersonating() ? 'impersonate' : 'uid', uid);
      return url.toString();
    } catch {
      return target;
    }
  }

  function selectOption(index: number) {
    selectedOption.value = index;
    const option = options.value[index];
    if (!option || !option.goto) return;
    if (/^https?:\/\//i.test(option.goto)) {
      sessionStorage.removeItem('redirect');
      window.location.href = option.goto;
      return;
    }
    router.push(option.goto);
  }

  return {
    options,
    selectOption,
  };
});
